import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

/** Synnergie support metrics (2026-09-29): SSE Bug / BreakFix mirrored
 *  from Jira with real In Progress → Done transitions. PMO and Admin only. */
export interface SupportMonth {
  month: string;
  created: Record<string, number>;
  created_total: number;
  resolved: number;
  open_end: number;
  days_in_progress_median: number | null;
  days_in_progress_p75: number | null;
  days_in_progress_n: number;
  cycle_days_median: number | null;
}
export interface SupportSummary {
  months: SupportMonth[];
  types: string[];
  totals: { issues: number; open_now: number; days_in_progress_median: number | null; days_in_progress_p75: number | null; hours_logged: number; with_hours: number };
  by_year: Record<string, { days_in_progress_median: number | null; days_in_progress_p75: number | null; n: number }>;
  assignees: Array<{ name: string; total: number; open: number }>;
  oldest_open: Array<{ issue_key: string; summary: string | null; issue_type: string; status: string | null; assignee: string | null; age_days: number; in_progress_days: number | null }>;
  synced_at: string | null;
  project_key: string;
  issue_types_tracked: string[];
  since: string;
}

export function useSupportMetrics(issueType?: string) {
  return useQuery<SupportSummary>({
    queryKey: ["support-metrics", issueType ?? "both"],
    queryFn: () => api.get("/support-metrics/", { params: issueType ? { issue_type: issueType } : {} }).then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useSyncSupportMetrics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/support-metrics/sync").then((r) => r.data as { synced: number }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["support-metrics"] }),
  });
}

/** One mirrored ticket, as stored. Powers the click-through lists. */
export interface SupportIssue {
  issue_key: string;
  issue_type: string;
  summary: string | null;
  status: string | null;
  status_category: string | null;
  assignee: string | null;
  priority: string | null;
  created_at: string;
  resolved_at: string | null;
  in_progress_at: string | null;
  done_at: string | null;
  days_in_progress: number | null;
  archived_at?: string | null;
}

export function useSupportIssues(issueType?: string) {
  return useQuery<SupportIssue[]>({
    queryKey: ["support-issues", issueType ?? "both"],
    queryFn: () => api.get("/support-metrics/issues", { params: issueType ? { issue_type: issueType } : {} }).then((r) => r.data),
    staleTime: 60_000,
  });
}
